import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { paymentsApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, ArrowRight } from 'lucide-react';

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [paymentData, setPaymentData] = useState(null);

  const { refreshUser } = useAuth();

  const sessionId = searchParams.get('session_id');
  const method = searchParams.get('method');
  const paymentId = searchParams.get('paymentId');
  const payerId = searchParams.get('PayerID');

  const checkPaymentStatus = useCallback(async () => {
    try {
      if (method === 'paypal' && paymentId && payerId) {
        const res = await paymentsApi.executePaypal(paymentId, payerId);

        if (res.data.status === 'success') {
          setSuccess(true);
          await refreshUser();
        }

        setLoading(false);
        return;
      }

      if (sessionId) {
        let attempts = 0;
        const maxAttempts = 5;

        const poll = async () => {
          try {
            const res = await paymentsApi.getStatus(sessionId);
            setPaymentData(res.data);

            if (res.data.payment_status === 'paid') {
              setSuccess(true);
              await refreshUser();
              setLoading(false);
              return;
            }

            attempts++;
            if (attempts < maxAttempts) {
              setTimeout(poll, 2000);
            } else {
              setLoading(false);
            }
          } catch (error) {
            console.error('Payment polling error:', error);
            setLoading(false);
          }
        };

        poll();
        return;
      }
    } catch (error) {
      console.error('Payment check error:', error);
      setLoading(false);
    }
  }, [method, paymentId, payerId, sessionId, refreshUser]);

  useEffect(() => {
    checkPaymentStatus();
  }, [checkPaymentStatus]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-[#00ff88] animate-spin mx-auto mb-4" />
          <p className="text-white text-lg">Verifying payment...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6"
      data-testid="payment-success-page"
    >
      <div className="max-w-md w-full text-center animate-fade-in">
        {success ? (
          <>
            <div className="w-24 h-24 rounded-full bg-[#00ff88]/20 flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
              <CheckCircle className="w-12 h-12 text-[#00ff88]" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">
              Payment Successful!
            </h1>

            <p className="text-gray-400 mb-8">
              Your balance has been updated with{' '}
              {paymentData?.amount_total
                ? `$${(paymentData.amount_total / 100).toFixed(2)}`
                : 'the payment amount'}
              .
            </p>

            <Link to="/dashboard">
              <Button
                className="h-14 px-8 bg-[#00ff88] text-black font-bold text-lg rounded-lg glow-green"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </>
        ) : (
          <>
            <div className="w-24 h-24 rounded-full bg-yellow-500/20 flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-yellow-500" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-4">Processing</h1>
            <p className="text-gray-400 mb-8">
              Payment verification is pending. Please check your dashboard.
            </p>

            <Link to="/dashboard">
              <Button
                className="h-14 px-8 bg-[#00ff88] text-black font-bold text-lg rounded-lg"
                data-testid="go-to-dashboard-btn"
              >
                Go to Dashboard
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
